import { Icon } from "react-migration/components/Icon";
import { ZooplaLogoLarge } from "react-migration/domains/comparables/img/ZooplaLogoLarge";
import cn from "classnames";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { OwnershipTags } from "react-migration/domains/comparables/components/Results/CompsListing/OwnershipTags";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import { ZooplaListingsDTO } from "react-migration/domains/comparables/typings/Zoopla";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { Button } from "react-migration/components/Button";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { saleAdvertURL } from "react-migration/domains/comparables/util/Links";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export const ComparableDetailHeader = ({
  record,
  zooplaData,
  zooplaListing,
  loading,
  address,
  noFullAccess,
}: {
  record?: IComparablesTransactionDTO;
  zooplaData?: ZooplaComparable;
  zooplaListing?: ZooplaListingsDTO;
  loading?: boolean;
  address?: string;
  noFullAccess: boolean;
}) => {
  const { t } = useTranslation();
  const isUSUser = hasFeature(Feature.usAccess);

  if (loading) return <SkeletonDetailHeader />;

  const enrichedRecord = {
    address,
    designation: record?.designation,
    tenure: record?.tenure,
    category: record?.category,
    sub_category: record?.sub_category,
    is_new_property: record?.is_new_property,
    zooplaEnrichment: {
      propertyType: zooplaData?.attributes?.propertyType,
      propertySubType: zooplaData?.attributes?.propertySubType,
      attributes: {
        tenure: zooplaData?.attributes?.tenure,
        newBuild: zooplaData?.attributes?.newBuild,
      },
    },
  };

  return (
    <div className="flex flex-col items-start justify-center w-full gap-2 bg-white">
      <div className="flex flex-row items-center justify-between w-full gap-10 cid-header-title-line">
        <div className="text-sm font-medium capitalize cid-header-title-line-address text-blue-grey-950">{address}</div>
        {!noFullAccess && (
          <Button
            variant="ghost"
            rel="noopener noreferrer"
            disabled={!zooplaListing?.listings?.latest?.url || noFullAccess}
            onClick={() => {
              logEventV2({
                name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
                properties: {
                  action: "goTo",
                  cardName: "Comparable Details",
                  clickDetail: "Zoopla market listing link",
                  uprn: zooplaData?.uprn?.toString(),
                  actionUrl: zooplaListing?.listings?.latest?.url || "No URL",
                },
              });

              if (!zooplaListing?.listings?.latest?.url || noFullAccess) return;

              window.open(zooplaListing?.listings?.latest?.url, "_blank");
            }}
            className={cn(
              "cid-header-title-line-link flex flex-row items-center justify-center gap-2 p-[2px]",
              zooplaListing?.listings?.latest?.url && "cursor-pointer",
              !zooplaListing?.listings?.latest?.url && "opacity-50 pointer-events-none"
            )}
          >
            <ZooplaLogoLarge className="h-[12px] w-auto" />
            <Icon icon="icon-lt-external-link-line" size="md" extraClasses="text-blue-grey-600" />
          </Button>
        )}
        {isUSUser && record?.address && (
          <Button
            variant="tertiary"
            size="lg"
            rel="noopener noreferrer"
            onClick={() => {
              logEventV2({
                name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
                properties: {
                  action: "goTo",
                  cardName: "Comparable Details",
                  clickDetail: "Zoopla market listing link",
                  uprn: zooplaData?.uprn?.toString(),
                  actionUrl: zooplaListing?.listings?.latest?.url || "No URL",
                },
              });

              window.open(saleAdvertURL({ address: record.address }), "_blank");
            }}
            className={cn(
              "cid-header-title-line-link flex flex-row items-center justify-center gap-2 p-[2px] cursor-pointer"
            )}
          >
            <span className="text-xs md:text-sm">{t("comparables.map.sidebar.value.advert")}</span>
            <Icon icon="icon-lt-external-link-line" size="md" extraClasses="text-blue-grey-600" />
          </Button>
        )}
      </div>

      <OwnershipTags
        transaction={enrichedRecord as EnrichedComparableDTO}
        status={
          noFullAccess ? "to_hide" : zooplaListing?.listings?.latest?.lifeCycleStatus // This is a temporary fix to show the correct status for the listing as the lifeCycleStatus is not always correct and sometimes the listing shows as for sale/rent but the listing date is from 2020 and the property is not on the market
        }
      />
    </div>
  );
};

const SkeletonDetailHeader = () => {
  return (
    <div className="relative h-16">
      <SkeletonLoading rows={1} showPadding={false} />;
    </div>
  );
};
